import React, { useEffect } from "react";
import "./App.css";

import { Amplify, API } from "aws-amplify";
import { useAuthenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
//import awsconfig from "./aws-exports"; performed in config updater
import Title from "./components/Title";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { LoginPage } from "./components/LoginPage";
import ProtectedRoutes from "./components/ProtectedRoutes";
import PageNotFound from "./components/PageNotFound";
import TitleLayout from "./components/TitleLayout";
import Home from "./components/Home";
import Services from "./components/Services";
import Customers from "./components/Customers";
import Ui from "./components/Ui";
import { UserProvider } from "./contexts/UserContextComponent";
import { fetchUsersByEmail } from "./dataaccess/fetchUsersByEmail";
import { LLMProject, LLMUser, UsersByEmailQuery } from "./API";
import Welcome from "./components/Welcome";
import { fetchLLMProject } from "./dataaccess/fetchLLMProject";
import ServiceForm from "./components/ServiceForm";
import { useQuery } from "@tanstack/react-query";
import ProjectForm from "./components/ProjectForm";
import CustomerForm from "./components/CustomerForm";
import { configUpdate } from "./configUpdate";

import Integration from "./components/Integration";
import ModelPricing from "./components/ModelPricing";
import ResponseQuality from "./components/ResponseQuality";
import ReactGA from "react-ga4";
import Router from "./components/Router";
import GroupForm from "./components/GroupForm";
import Prompts from "./components/Prompts";
import PromptForm from "./components/PromptForm";
import EnterpriseChat from "./components/EnterpriseChat";
import Enterprise from "./components/Enterprise";
import Logging from "./components/Logging";
import ChatTest from "./components/ChatTest";
import PlansAndUpgrade from "./components/PlansAndUpgrade";
import { updateLLMProjectMaintenanceMode } from "./dataaccess/updateProjectMaintenanceMode";
import Agents from "./components/Agents";
import AgentForm from "./components/AgentForm";
import ConversationsAndCallsPage from "./components/ConversationAndCallsPage";
import AdminHome from "./components/admin/AdminHome";
import ModelsPublic from "./components/admin/ModelsPublic";
import EmbedAgentComponentNew from "./components/EmbedAgentComponentNew";
import ScheduledPromptForm from "./components/ScheduledPromptForm";
import Triggers from "./components/Triggers";
import ServiceManagement from "./components/ServiceManagement";

function App() {
  //Amplify.configure(awsconfig);
  Amplify.configure({ ...configUpdate, ssr: true });
  ReactGA.initialize("G-88HF8ZMC34");

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthenticator((context) => [context.user]);
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const [currentUser, setCurrentUser] = React.useState<
    LLMUser | undefined | null
  >(null);

  const [userProjectsList, setUserProjectsList] = React.useState<LLMUser[]>([]);
  const [entitlements, setEntitlements] = React.useState<string[] | undefined>(
    []
  );

  const [subscriptionActive, setSubscriptionActive] =
    React.useState<boolean>(false);

  const [trialDaysRemaining, setTrialDaysRemaining] = React.useState<number>(0);

  const { data } = useQuery({
    queryKey: ["projects", { id: currentUser?.projectId ?? "" }],
    queryFn: () => fetchLLMProject(currentUser?.projectId ?? ""),
    enabled: currentUser?.projectId ? true : false,
  });

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname });
    console.log("pageview", location.pathname);
  }, [location]);

  /************************************************
   * user changed, lookup our data about that username
   * they might be multiple org records for this user,
   * so use the last one that had (local storage),
   * or the first one (no local storage),
   * or send to "welcome" if they have none
   ************************************************/
  useEffect(() => {
    // user has changed, and might not be authenticated
    if (authStatus === "authenticated" && user) {
      fetchUsersByEmail(
        user.attributes?.email
          ? user.attributes?.email.toLowerCase()
          : user.getUsername().toLowerCase()
      )
        .then((user) => {
          if (user.data) {
            const newUser: UsersByEmailQuery = user.data;
            if (
              newUser.usersByEmail?.items &&
              newUser.usersByEmail.items.length > 0
            ) {
              const projects = newUser.usersByEmail.items as LLMUser[];
              setUserProjectsList(projects);

              console.log("user projects", projects);

              // Select project after we have the projects list
              const storedProjectId = localStorage.getItem("selectedProjectId");
              let selectedUser: LLMUser;

              // Try to find the stored project first
              if (storedProjectId) {
                const foundProject = projects.find(
                  (project: LLMUser) => project?.id === storedProjectId
                );
                if (foundProject) {
                  console.log(
                    "Restored project from localStorage:",
                    foundProject.project?.name
                  );
                  selectedUser = foundProject;
                } else {
                  console.log("Stored project not found, using first project");
                  selectedUser = projects[0];
                  // Update localStorage with valid project
                  localStorage.setItem(selectedUser.id, selectedUser.id);
                }
              } else {
                console.log("No stored project, using first project");
                selectedUser = projects[0];
                // Store the selected project
                localStorage.setItem(selectedUser.id, selectedUser.id);
              }

              setCurrentUser(selectedUser);
            } else {
              // no orgs for this user, send to welcome page
              localStorage.removeItem("selectedProjectId");
              navigate("/welcome", { replace: true });
            }
          }
        })
        .catch((err) => {
          console.log("error fetching user", err);
          localStorage.removeItem("selectedProjectId");
          user.signOut();
        });
    } else {
      if (authStatus === "unauthenticated") {
        setCurrentUser(null);
        localStorage.removeItem("selectedProjectId");
      }
    }
  }, [authStatus, user, navigate]);

  //const currentProject = data?.data?.getLLMProject as LLMProject | undefined;

  useEffect(() => {
    if (!currentUser) return;

    // special case, first time login status="invited", send them to the welcome page
    if (currentUser?.status === "invited") {
      navigate("/welcome", { replace: true });
      return;
    }
  }, [currentUser]);

  /* example customer for LLMServiceProvider
  const customer = {
    customer_id: currentProject?.id ?? "",
    customer_name: currentProject?.name ?? "",
    customer_user_id: currentUser?.id ?? "",
  };
  */

  function isSubscriptionActive() {
    /*
incomplete: The subscription is still being created and is not yet active.
incomplete_expired: The subscription was not created successfully and has expired.
trialing: The subscription is in trial mode and has not yet been charged. 
* active: The subscription is active and has been charged.
past_due: The subscription has a payment that is past due.
* canceled: The subscription has been canceled and will not be charged again. (can the renew after end date? might need to say yes to this one)
unpaid: The subscription has not been paid and is now considered unpaid.*/

    if (!currentProject) return false;
    if (!currentProject.subscription) return false;

    const customerSubscription = currentProject.subscription;

    if (
      customerSubscription.subscriptionStatus &&
      customerSubscription.subscriptionStatus === "active"
    )
      return true;

    if (
      customerSubscription.subscriptionStatus &&
      customerSubscription.subscriptionStatus === "canceled"
    ) {
      const currentDate = new Date();
      const subscriptionEndedAt = customerSubscription?.subscriptionEndedAt
        ? new Date(customerSubscription?.subscriptionEndedAt)
        : null;

      if (subscriptionEndedAt && subscriptionEndedAt <= currentDate)
        return false;
      else return true; // canceled but not yet expired
    }

    return false;
  }

  const calculateTrialDaysRemaining = () => {
    if (!currentProject) return 0;

    const start = new Date(currentProject.createdAt);
    const end = new Date(start);
    end.setDate(start.getDate() + 30);

    const diffTime = Math.max(end.getTime() - new Date().getTime(), 0);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  const currentProject = data?.data?.getLLMProject as LLMProject | undefined;

  useEffect(() => {
    if (!currentProject) {
      setEntitlements([]);
      setSubscriptionActive(false);
      setTrialDaysRemaining(0);
      return;
    }

    const subActive = isSubscriptionActive();
    const trialDays = calculateTrialDaysRemaining();

    setSubscriptionActive(subActive);
    setTrialDaysRemaining(trialDays);

    // if trial ended and no subscription, set the project to maintenance mode and after 12/3/2024 then disable
    if (
      !subActive &&
      trialDays <= 0 &&
      currentProject.createdAt > "2024-12-03"
    ) {
      updateLLMProjectMaintenanceMode(currentProject.id, true);
    }

    const fetchEntitlements = async () => {
      const result = await API.post("eventapi", "/activeEntitlements", {
        body: { customerId: currentProject.stripeCustomerId },
      });

      console.log("entitlements", result);
      setEntitlements(result);
    };

    if (
      !currentProject?.stripeCustomerId ||
      currentProject.stripeCustomerId === ""
    ) {
      setEntitlements([]);
    } else {
      fetchEntitlements();
    }
  }, [currentProject]);

  return (
    <div className="App">
      <UserProvider
        user={currentUser}
        setUser={setCurrentUser}
        project={currentProject}
        projects={userProjectsList}
        entitlements={entitlements}
        subscriptionActive={subscriptionActive}
        trialDaysRemaining={trialDaysRemaining}
      >
        <Routes>
          <Route
            path="/icon-72x72.png"
            element={<img src="/icon-72x72.png" alt="icon" />}
          />

          <Route path="/login" element={<LoginPage />} />
          <Route path="/enterprise/:id" element={<EnterpriseChat />} />
          <Route path="/embedagent" element={<EmbedAgentComponentNew />} />
          <Route path="/models" element={<ModelsPublic />} />

          <Route element={<ProtectedRoutes />}>
            <Route
              path="/welcome"
              element={
                <>
                  <Title />
                  <Welcome />
                </>
              }
            />
            <Route
              path="/welcome/new"
              element={
                <>
                  <Title />
                  <Welcome addNew={true} />
                </>
              }
            />{" "}
            <Route element={<TitleLayout />}>
              <Route path="/services" element={<ServiceManagement />} />

              <Route
                path="/conversations"
                element={<ConversationsAndCallsPage />}
              />
              <Route path="/chat" element={<ChatTest />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customers/edit/:id" element={<CustomerForm />} />
              <Route path="/customers/new" element={<CustomerForm />} />
              <Route path="/ui" element={<Ui />} />
              <Route path="/services/edit/:id" element={<ServiceForm />} />
              <Route path="/services/new" element={<ServiceForm />} />
              <Route path="/projects/edit/:id" element={<ProjectForm />} />
              <Route path="/integration" element={<Integration />} />
              <Route path="/quality" element={<ResponseQuality />} />
              <Route path="/route" element={<Router />} />
              <Route path="/groups/edit/:id" element={<GroupForm />} />
              <Route path="/groups/new" element={<GroupForm />} />
              <Route path="/prompts" element={<Prompts />} />
              <Route path="/prompts/edit/:id" element={<PromptForm />} />
              <Route path="/prompts/new" element={<PromptForm />} />
              <Route path="/modelpricing" element={<ModelPricing />} />
              <Route path="/enterpriseconfig" element={<Enterprise />} />
              <Route path="/logging" element={<Logging />} />
              <Route path="/plans" element={<PlansAndUpgrade />} />
              <Route path="/agents" element={<Agents />} />
              <Route path="/agents/:id" element={<Agents />} />
              <Route path="/agents/edit/:id" element={<AgentForm />} />
              <Route path="/agents/new" element={<AgentForm />} />
              <Route path="/triggers" element={<Triggers />} />

              <Route
                path="/scheduledprompts/new"
                element={<ScheduledPromptForm />}
              />
              <Route
                path="/scheduledprompts/edit/:id"
                element={<ScheduledPromptForm />}
              />

              <Route path="/adminhome" element={<AdminHome />} />

              <Route index element={<Home />} />

              <Route path="*" element={<PageNotFound />} />
            </Route>
          </Route>
        </Routes>
      </UserProvider>
    </div>
  );
}

export default App;

/*
        <LLMServiceProvider project_id={currentProject?.id}>
                </LLMServiceProvider> */
